import { ArrayOpType, CommentType, ReorderOpType, ShapeType } from '@tencent/slide-sdk-pb-model';
import { assert } from 'ts-essentials';
import { MoveShapeMutation } from '../mutation/shape/move-shape-mutation';
import { getGroupTransform, getUngroupedTransform, Frame } from '../utils/transform-utils';
import { UngroupMutation } from '../mutation/shape/ungroup-mutation';
import { SetShapePropertiesMutation, RemoveShapeMutation } from '../mutation/shape';
import { TransformShapeMutation } from '../mutation/shape/transform-shape-mutation';
import { Rule } from '../mutation/base/mutation-rules';
import { record } from '../utils/recorder';
import { BaseShapePropertiesProps } from '../props/shape/shape-props';
import { DataType, DataTypeUtils } from '../data';
import { BaseShapeMutationAPI } from './base-shape-mutation-api';
import type { IGroupShapeData } from '../data';
import type { IDrawingShapeDataModel, GroupShapeDataModel } from '../model/group-shape-data-model';
import type { ICTGroupTransform2D, ICTShapePropertiesDelta } from '@tencent/slide-sdk-pb-model';
import type { DeltaOf } from '../utils/delta';
import type { GroupShapePropertiesProps } from '../props/shape/group-shape-props';
import type { IFillProps } from '../props/common/fill-props';
import type { ISlideDataModel } from '../model/slide-data-model';

export class GroupShapeMutationAPI extends BaseShapeMutationAPI<GroupShapeDataModel> {
    /**
     * 更新组合属性
     */
    @record()
    public updateProps({ spPr, nvPr }: DeltaOf<Omit<IGroupShapeData, 'shapeList'>>) {
        super.updateProps({
            spPr,
            nvPr: nvPr && super.getCommonNonVisualPropsDelta(nvPr),
        });
    }

    /**
     *
     * 调整形状层级，相对于各形状的自身位置进行调整。
     * 调整后，形状之间的相对顺序保持不变。
     * @param shapes 待调整的形状集合，必须是 GroupShape 的 shapeList 的子集。
     * @param opType 调整类型，支持：前移、后移、置顶、置底
     */
    @record()
    public reorderShapes(shapes: IDrawingShapeDataModel[], opType: ReorderOpType) {
        this.guard((presentation) =>
            Rule.forEach(shapes, (shape) => Rule.shape.is.childOfGroupShape(presentation, shape?.id, this.model.id)),
        );

        const { shapeList } = this.model.store;

        const movingShapesSet = new Set(shapes);

        let begin = 0;
        let end = shapeList.length - 1;

        // 后移时，跳过已经置底的形状
        if (opType === ReorderOpType.Backward) {
            while (movingShapesSet.has(shapeList.at(begin))) {
                begin += 1;
            }
        }

        // 前移时，跳过已经置顶的形状
        if (opType === ReorderOpType.Forward) {
            while (movingShapesSet.has(shapeList.at(end))) {
                end -= 1;
            }
        }

        // 前移或置底时，后面的元素需要先移动，否则会出现形状顺序错乱的问题
        const movingShapeIds = (
            opType === ReorderOpType.Forward || opType === ReorderOpType.Back // 前移或置底时，后面的元素需要先移动，否则会出现形状顺序错乱的问题
                ? shapeList.toArray().slice().reverse()
                : shapeList.toArray()
        ).filter((shape, index) => {
            return movingShapesSet.has(shape) && index >= begin && index <= end;
        });

        movingShapeIds.forEach((shape) => this.reorderShape(shape, opType));
    }

    /**
     * 调整形状层级，统一调整到同一指定位置。
     * 调整后，形状之间的相对顺序保持不变
     * @param shapes 待调整的形状集合，必须是 GroupShape 的 shapeList 的子集。
     * @param toIndex 目标位置
     */
    @record()
    public moveShapes(shapes: IDrawingShapeDataModel[], toIndex: number) {
        this.guard((presentation) =>
            Rule.every(
                Rule.groupShape.can.insertShapeToPosition(presentation, this.model.id, toIndex),
                Rule.forEach(shapes, (shape) => Rule.shape.is.childOfGroupShape(presentation, shape.id, this.model.id)),
            ),
        );

        const { shapeList } = this.model.store;
        const movingShapesSet = new Set(shapes);

        // 记录每个 Shape 左边有多少个无需移动的 Shape
        const otherShapeCounts: number[] = [0];

        /**
         * 类似冒泡排序，需要移动的 Shape 向目标位置移动时，挤走中间不需要移动的，即为最终结果。
         */
        const bubbleMoveShapeOfIndex = (fromIndex: number, toIndex: number) => {
            const shape = shapeList.at(fromIndex);
            if (!movingShapesSet.has(shape)) return;

            // 计算当前移动的 Shape 到目标位置有多少个无需移动的 Shape，即移动的偏移量
            const offset = otherShapeCounts[toIndex] - otherShapeCounts[fromIndex];
            if (!offset) return;

            this.reorderShape(shape, offset > 0 ? ReorderOpType.Forward : ReorderOpType.Backward, Math.abs(offset));
        };

        this.forEachInTransaction(shapeList.toArray(), (shape, index) => {
            otherShapeCounts.push(otherShapeCounts[index] + Number(!movingShapesSet.has(shape)));

            // 从目标位置向前回溯，逐一移动最小距离
            if (index === toIndex - 1) {
                for (let i = toIndex; i >= 0; i--) {
                    bubbleMoveShapeOfIndex(i, toIndex);
                }
            }

            if (index >= toIndex) {
                bubbleMoveShapeOfIndex(index, toIndex);
            }
        });
    }

    /**
     * 删除组合内图形，形状包括嵌套的组合图形
     * @param shapes 待删除的形状
     */
    @record()
    public deleteShapes(shapes: IDrawingShapeDataModel[]) {
        this.guard((presentation) =>
            Rule.forEach(shapes, (shape) =>
                Rule.shape.should.existInPage(presentation, shape.id, this.model.getPage().id),
            ),
        );

        // 需要更新 transform 的组合图形
        const groupsNeedUpdateTransform = new Set<string>();

        const presentationModels = this.getPresentation().store.models;
        // 1. 对每一层组合执行删除：从内层往外层删除，因为内层删为空组合后可能会把组合也删除
        for (const shape of shapes.slice()) {
            // 先记录影响范围，顺序是从外层到内层
            shape.forEachAncestorShapes(
                (group) => {
                    groupsNeedUpdateTransform.add(group.id);
                },
                { reverse: true },
            );
            // 删除元素
            // eslint-disable-next-line complexity
            this.transaction(() => {
                const groupShape = presentationModels.get(shape.parent.id, DataType.GroupShape);
                if (!groupShape) {
                    return;
                }
                const leftShapes: IDrawingShapeDataModel[] = groupShape.shapeList.filter(
                    (child) => child.id !== shape.id,
                );
                // spTree 的 parent 不是 GroupShape，spTree 需要简单处理: 直接把元素移除即可
                if (groupShape?.parent.type !== DataType.GroupShape) {
                    groupShape.api.removeShapes([shape]);
                    return;
                }
                if (leftShapes.length === 0) {
                    // 组合里的元素全被删了，直接把这个组合删掉
                    groupShape.parent.api.removeShapes([groupShape]);
                    return;
                }
                if (leftShapes.length === 1) {
                    // 组合里的元素删完只剩一个，组合不需要保留，直接解组即可
                    groupShape.api.ungroup();
                    groupShape.parent.api.removeShapes([shape]);
                    return;
                }
                groupShape.api.removeShapes([shape]);
            });
        }

        // 2. 更新组合的 transform 信息：需要从子元素向父组合建立 transform 关系
        for (const groupId of Array.from(groupsNeedUpdateTransform).reverse()) {
            const groupShape = presentationModels.get(groupId, DataType.GroupShape);
            if (!groupShape) {
                continue;
            }
            // 计算 group 新的 transform 信息: 先把剩下的元素组合，再计算新组合的 transform 即可
            const groupTransform = getGroupTransform(groupShape.shapeList.map((shape) => shape.getFrame()));
            const newTransform = getUngroupedTransform(groupShape.getFrame(), Frame.fromTransform(groupTransform));
            groupShape.api.refreshTransform({
                ...newTransform,
                chOff: groupTransform.chOff,
                chExt: groupTransform.chExt,
            });
        }
    }

    /**
     * 组合更新 transform 信息
     */
    @record()
    public refreshTransform(GroupTransform: ICTGroupTransform2D) {
        this.transaction(() => {
            const { off, ext, chOff, chExt } = GroupTransform;
            this.request([
                new TransformShapeMutation({
                    shapeId: this.model.id,
                    shapeType: ShapeType.GroupShape,
                    pageId: this.model.getPage().id,
                    parentId: this.model.parent.id,
                    xfrmDelta: { off, ext },
                    chXfrmDelta: { chOff, chExt },
                }),
            ]);
        });
    }

    /**
     * 解组自己，并执行以下操作：
     * 1. 解组后，子元素会重新计算 Transform；
     * 2. 如果组合有填充，且子元素设置为组合填充，解组后的子元素会设置为组合的填充；
     * @param groupShape 待解组的组合
     */
    @record()
    public ungroup() {
        this.guard((presentation) => Rule.shape.is.typeOf(presentation, this.model.id, DataType.GroupShape));

        const { parent } = this.model;
        assert(parent.type === DataType.GroupShape, 'parent should be group shape');

        this.transaction(() => {
            this.model.getPage().timing?.api.removeAnimationsByShapeId(this.model.id);

            this.request([
                new UngroupMutation({
                    groupShapeId: this.model.id,
                    pageId: this.model.getPage().id,
                    shapeIds: this.model.shapeList.map((shape) => shape.id),
                    parentId: parent.id,
                    index: parent.store.shapeList.findIndexById(this.model.id),
                }),
            ]);

            this.model.shapeList.forEach((shape) => {
                const { off, ext, rot, flipH, flipV } = getUngroupedTransform(this.model.getFrame(), shape.getFrame());

                if (
                    this.model.props.spPr instanceof BaseShapePropertiesProps &&
                    shape.props.spPr instanceof BaseShapePropertiesProps
                ) {
                    const spPr = this.getUnGroupSpPr(this.model.props.spPr, shape.props.spPr);
                    if (Object.keys(spPr).length > 0) {
                        this.request([
                            new SetShapePropertiesMutation({
                                shapeId: shape.id,
                                pageId: this.model.getPage().id,
                                shapeDelta: {
                                    spPr,
                                },
                            }),
                        ]);
                    }
                }

                this.request([
                    new TransformShapeMutation({
                        shapeId: shape.id,
                        shapeType: DataTypeUtils.toShapeType(shape.type),
                        pageId: this.model.getPage().id,
                        parentId: this.model.parent.id,
                        xfrmDelta: { off, ext, rot, flipH, flipV },
                    }),
                ]);
            });

            this.updateDeletedModelComment([this.model.id]);
        });
    }

    private getUnGroupSpPr(groupSpPr: GroupShapePropertiesProps, shapeSpPr: BaseShapePropertiesProps<IFillProps>) {
        let unGroupSpPr: ICTShapePropertiesDelta = {};
        const { scene3d, fill: groupFill } = groupSpPr;
        if (shapeSpPr.grpFill && !groupFill.isEmpty()) {
            unGroupSpPr = { ...groupFill.getDelta() };
        }
        if (scene3d) {
            unGroupSpPr.scene3d = scene3d;
            if (unGroupSpPr.scene3d.extLst?.ext) {
                unGroupSpPr.scene3d.extLst.extOp = { opType: ArrayOpType.Set };
            }
            if (unGroupSpPr.scene3d.backdrop?.extLst?.ext) {
                unGroupSpPr.scene3d.backdrop.extLst.extOp = { opType: ArrayOpType.Set };
            }
        }
        return unGroupSpPr;
    }

    private reorderShape(shape: IDrawingShapeDataModel, opType: ReorderOpType, step = 1) {
        const { shapeList } = this.model.store;
        this.request(
            [
                new MoveShapeMutation({
                    pageId: this.model.getPage().id,
                    shapeId: shape.id,
                    srcParentId: this.model.id,
                    destParentId: this.model.id,
                    srcIndex: shapeList.findIndexById(shape.id),
                    destIndex: shapeList.findReorderDestinationIndex(shape.id, opType, step),

                    // @TODO deprecated
                    opType: 0,
                    destShapeId: '',
                }),
            ],
            true,
        );
    }

    /**
     * 单纯移除图形，不包括 UI 层删除图形时带来的各种副作用
     */
    private removeShapes(shapes: IDrawingShapeDataModel[]) {
        this.guard((presentation) =>
            Rule.forEach(shapes, (shape) => Rule.shape.is.childOfGroupShape(presentation, shape.id, this.model.id)),
        );

        this.transaction(() => {
            for (const shape of shapes) {
                this.model.getPage().timing?.api.removeAnimationsByShapeId(shape.id);

                if (shape.type === DataType.GroupShape) {
                    const shapeList = shape.shapeList.slice();
                    shape.api.ungroup();
                    this.removeShapes(shapeList);
                    this.updateDeletedModelComment([shape.id]);
                } else {
                    // 普通形状直接移除
                    this.request(
                        [
                            new RemoveShapeMutation({
                                shapeId: shape.id,
                                pageId: this.model.getPage().id,
                                parentId: this.model.id,
                                index: this.model.store.shapeList.findIndexById(shape.id),
                            }),
                        ],
                        true,
                    );
                    this.updateDeletedModelComment([shape.id]);
                }
            }
        });
    }

    private updateDeletedModelComment(deletedModelIds: string[]) {
        // 更新组合的 comment 信息： 将被删除的所有元素批注变更为背景批注
        if (this.model.getPage().type === DataType.Slide && deletedModelIds.length > 0) {
            const slideDataModel = this.model.getPage() as ISlideDataModel;
            const { commentGroupMap } = slideDataModel.props;
            if (!commentGroupMap) return;
            Object.keys(commentGroupMap).forEach((groupId) => {
                const { shapeId } = commentGroupMap[groupId];
                if (shapeId && deletedModelIds.includes(shapeId)) {
                    slideDataModel.api.setCommentsProperties([
                        {
                            groupId,
                            commentGroup: {
                                commentType: CommentType.slide,
                                shapeId, // 冲突处理需要
                            },
                        },
                    ]);
                }
            });
        }
    }
}
